import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";

import { Participant } from "@asd/customer-portal-api";
import { Footer, getNavigationMap, getNavigationTitle, HelpLink, NavigationRoute, useOrganization } from "@asd/customer-portal-ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Backdrop,
  Badge,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { snapdom } from "@zumer/snapdom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useLegalAgreements } from "~/hooks/useLegalAgreements";
import { useProfileLink } from "~/hooks/useProfileLink";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import MessagesProvider, { useMessages } from "~/providers/MessagesProvider";
import { env } from "~/utils/env";
import { isUserParticipant, isUserProvider } from "~/utils/keycloakHelpers";
import { LayoutRoutes, useKeycloak } from "../authorization";
import { DashboardStaffProvider, useDashboardStaffProvider } from "./authorized/dashboard_staff/DashboardStaffProvider";

const params = new URLSearchParams(window.location.search);
const participantId = params.get("participantId");

if (participantId) {
  sessionStorage.setItem("pending_participant_id", participantId);
}

interface NavigationProps {
  toggleThemeMode: () => void;
}

const NavigationWrapper = (WrappedComponent: FC<NavigationProps>) => {
  const WrappedNavigation = (props: any) => {
    const keycloak = useKeycloak();

    let MessagesProviderWrapper: React.ElementType = Fragment;
    let DashboardStaffProviderWrapper: React.ElementType = Fragment;

    if (keycloak.authenticated) {
      MessagesProviderWrapper = MessagesProvider;

      if (!isUserParticipant(keycloak) && !isUserProvider(keycloak)) {
        DashboardStaffProviderWrapper = DashboardStaffProvider;
      }
    }

    return (
      <MessagesProviderWrapper>
        <DashboardStaffProviderWrapper>
          <WrappedComponent {...props} />
        </DashboardStaffProviderWrapper>
      </MessagesProviderWrapper>
    );
  };

  return WrappedNavigation;
};

export const DRAWER_WIDTH_CLOSED = 50;
const DRAWER_WIDTH_OPEN = 220;

const Navigation: FC<NavigationProps> = ({ toggleThemeMode }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const keycloak = useKeycloak();
  const {
    configuration: {
      branding: { logoSrc },
      ppt: { showCareProviders: showCareProvidersForPPT },
    },
  } = useOrganization();

  const LegalAgreements = useLegalAgreements();
  const UserPreferencesTemp = useUserPreferences();
  const UserPreferences = env.REACT_APP_WITH_MESSAGES !== "false" ? UserPreferencesTemp : false;

  const navigate = useNavigate();
  const location = useLocation();

  const { onProfileNavigate } = useProfileLink();
  const { participantDetails, selectedParticipant, setSelectedParticipant } = useDashboardStaffProvider();
  const { totalUnread } = useMessages();

  const { isInitialized, lastPayDate, participant } = useSelector(
    (state: { ConfigurationSlice: { isInitialized: boolean; lastPayDate?: string; participant?: Participant } }) =>
      state.ConfigurationSlice,
  );
  const { downloadImageTime } = useSelector((state: { GeneralSlice: { downloadImageTime: number } }) => state.GeneralSlice);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);

  const layoutContainerRef = useRef(null);
  const lastDownloadImageTime = useRef(0);

  const navigationMap = useMemo(
    () =>
      getNavigationMap({
        roles: keycloak.realmAccess?.roles ?? [],
        groups: keycloak.userInfo?.groups ?? [],
        showCareProvidersForPPT,
        withProviderRegistry: env.REACT_APP_WITH_PROVIDER_REGISTRY !== "false",
        withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
        withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
        withReports: env.REACT_APP_WITH_REPORTS !== "false",
        getParticipantLabel: () =>
          selectedParticipant ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}` : "Participant/Member",
        isParticipantSelected: () => !!selectedParticipant,
      }),
    [keycloak.realmAccess?.roles, keycloak.userInfo?.groups, showCareProvidersForPPT, selectedParticipant],
  );

  const seo = useMemo(() => {
    const title = getNavigationTitle({
      roles: keycloak.realmAccess?.roles ?? [],
      groups: keycloak.userInfo?.groups ?? [],
      isAuthenticated: !!keycloak.authenticated,
      showCareProvidersForPPT,
      withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
      withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
      withReports: env.REACT_APP_WITH_REPORTS !== "false",
      getParticipantLabel: () =>
        selectedParticipant ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}` : "Participant/Member",
      isParticipantSelected: () => !!selectedParticipant,
      pathname: location.pathname,
    });
    const flatRoutes = navigationMap.flat() as NavigationRoute[];
    const route = flatRoutes.find((entry) => entry && "route" in entry && entry.route === location.pathname);

    return {
      title: title ? `${title} | mySD Hub` : "Customer Portal | mySD Hub",
      description:
        route?.description ?? "Secure provider portal for care coordination, registration, budget tracking, and service management.",
    };
  }, [keycloak, location.pathname, showCareProvidersForPPT, navigationMap, selectedParticipant]);

  useEffect(() => {
    document.title = seo.title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMetaTag("description", seo.description);

    const ogTitle = document.querySelector(`meta[property="og:title"]`) as HTMLMetaElement | null;
    if (ogTitle) {
      ogTitle.setAttribute("content", seo.title);
    }
    const ogDescription = document.querySelector(`meta[property="og:description"]`) as HTMLMetaElement | null;
    if (ogDescription) {
      ogDescription.setAttribute("content", seo.description);
    }

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}${location.pathname}`);
  }, [seo, location.pathname]);

  useEffect(() => {
    if (downloadImageTime !== lastDownloadImageTime.current && layoutContainerRef.current) {
      lastDownloadImageTime.current = downloadImageTime;
      setDownloadingImage(true);

      const downloadPng = async () => {
        await snapdom.download(layoutContainerRef.current!, {
          format: "png",
          filename: "dashboard",
          quality: 1,
          backgroundColor: theme.palette.background.paper,
        });
        setDownloadingImage(false);
      };

      setTimeout(() => {
        downloadPng();
      }, 500);
    }
  }, [dispatch, downloadImageTime, theme]);

  useEffect(() => {
    const participantId = sessionStorage.getItem("pending_participant_id");

    if (participantId && participantDetails?.length) {
      setSelectedParticipant(participantDetails.find(({ participantId: pid }) => participantId === pid) ?? null);
      sessionStorage.removeItem("pending_participant_id");
    }
  }, [participantDetails, setSelectedParticipant]);

  return (
    <Stack direction="column" height="100%" width="100%">
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ pl: { sm: 2, md: 2 }, display: "grid", gridTemplateColumns: "1fr auto 1fr" }} variant="dense">
          <Stack alignItems="center" direction="row" height="inherit" spacing={1}>
            {!!keycloak.authenticated && (
              <Tooltip title={isDrawerOpen ? "Close Navigation Drawer" : "Open Navigation Drawer"}>
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={() => {
                    setIsDrawerOpen((prevOpen) => !prevOpen);
                  }}
                  sx={[
                    {
                      marginRight: 1,
                      position: "relative",
                    },
                  ]}
                >
                  <ArrowBackIcon sx={{ opacity: 0 }} />
                  <ArrowBackIcon
                    sx={[
                      {
                        position: "absolute",
                        transition: (theme) =>
                          theme.transitions.create(["transform", "opacity"], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                          }),
                      },
                      !isDrawerOpen && {
                        transform: "rotate(180deg)",
                        opacity: 0,
                      },
                    ]}
                  />
                  <MenuIcon
                    sx={[
                      {
                        position: "absolute",
                        transition: (theme) =>
                          theme.transitions.create(["transform", "opacity"], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                          }),
                      },
                      isDrawerOpen && {
                        transform: "rotate(-180deg)",
                        opacity: 0,
                      },
                    ]}
                  />
                </IconButton>
              </Tooltip>
            )}
            <Box
              alt="Logo"
              component="img"
              onClick={() => {
                if (keycloak.authenticated) {
                  navigate("/", { replace: true });
                }
              }}
              src={logoSrc}
              sx={[
                {
                  height: `calc(100% - ${theme.spacing(2)})`,
                  p: 0.5,
                  mr: 1,
                  background: theme.palette.info.main,
                  borderRadius: "100%",
                },
                !!keycloak.authenticated && {
                  cursor: "pointer",
                },
              ]}
            />
            <Typography className="black-shadow" variant="h6" noWrap>
              {getNavigationTitle({
                roles: keycloak.realmAccess?.roles ?? [],
                groups: keycloak.userInfo?.groups ?? [],
                isAuthenticated: !!keycloak.authenticated,
                showCareProvidersForPPT,
                withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
                withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
                withReports: env.REACT_APP_WITH_REPORTS !== "false",
                getParticipantLabel: () =>
                  selectedParticipant ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}` : "Participant/Member",
                isParticipantSelected: () => !!selectedParticipant,
                pathname: location.pathname,
              })}
            </Typography>
          </Stack>
          <Typography className="black-shadow" variant="h6" noWrap>
            {!keycloak.authenticated || !isInitialized || !isUserParticipant(keycloak)
              ? ""
              : Object.keys(participant ?? {}).length > 0
                ? `Viewing: ${participant!.firstName ?? ""} ${participant!.lastName ?? ""}`
                : `${keycloak.userInfo!.given_name} ${keycloak.userInfo!.family_name}`}
          </Typography>
          <Box justifyItems="end" overflow="hidden">
            <HelpLink className="black-shadow" color="primary.contrastText" variant="h6" />
          </Box>
        </Toolbar>
      </AppBar>
      <Stack direction="column" height="100%" width="100%">
        <Stack direction="row" flexGrow={1} height={0} width="100%">
          {(!!keycloak.authenticated || !!keycloak.onVerificationCallback) && (
            <Drawer
              open={isDrawerOpen}
              sx={{
                zIndex: (theme) => theme.zIndex.drawer,
                height: "100%",
                width: isDrawerOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
                flexShrink: 0,
                whiteSpace: "nowrap",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                [`& .MuiDrawer-paper`]: {
                  position: "static",
                  width: isDrawerOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
                  boxSizing: "border-box",
                  overflowX: "hidden",
                  transition: (theme) =>
                    theme.transitions.create("width", {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
                },
              }}
              variant="permanent"
            >
              <Toolbar variant="dense" />
              <Stack direction="column" height="100%">
                {navigationMap.map((listItems, index) => {
                  if ("grow" in listItems) {
                    const enableThemeToggle = false;

                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={index}>
                        <Box key="grow" flexGrow={1} />
                        {enableThemeToggle && isInitialized && (
                          <Tooltip title={`Change to ${theme.palette.mode === "light" ? "Dark" : "Light"} theme`}>
                            <ListItem sx={{ display: "block" }} disablePadding>
                              <ListItemButton
                                onClick={toggleThemeMode}
                                sx={{
                                  minHeight: 48,
                                  px: 1.5,
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 3,
                                    justifyContent: "center",
                                  }}
                                >
                                  {theme.palette.mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                                </ListItemIcon>
                                <ListItemText
                                  primary={`${theme.palette.mode === "light" ? "Light " : "Dark "}Mode`}
                                  sx={{
                                    opacity: isDrawerOpen ? 1 : 0,
                                    transition: (theme) =>
                                      theme.transitions.create("opacity", {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen * 2,
                                      }),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          </Tooltip>
                        )}
                      </Fragment>
                    );
                  }

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <List key={index}>
                      {listItems.map((item: NavigationRoute) => {
                        const { title, Icon, route, isDisabled } = item;

                        if ((!isInitialized && route !== "/logout") || (!keycloak.authenticated && !keycloak.onVerificationCallback)) {
                          return null;
                        }

                        const titleToUse = getNavigationTitle({
                          roles: keycloak.realmAccess?.roles ?? [],
                          groups: keycloak.userInfo?.groups ?? [],
                          isAuthenticated: !!keycloak.authenticated,
                          showCareProvidersForPPT,
                          withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
                          withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
                          withReports: env.REACT_APP_WITH_REPORTS !== "false",
                          getParticipantLabel: () =>
                            selectedParticipant ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}` : "Participant/Member",
                          isParticipantSelected: () => !!selectedParticipant,
                          title: typeof title === "function" ? title({ selectedParticipant }) : title,
                        });

                        return (
                          <Tooltip key={titleToUse} title={titleToUse}>
                            <ListItem sx={{ display: "block" }} disablePadding>
                              <ListItemButton
                                disabled={isDisabled ? isDisabled() : false}
                                onClick={() => {
                                  if (route === "/logout") {
                                    keycloak.logout();
                                  } else if (route === "/profile") {
                                    onProfileNavigate();
                                  } else if (route === "/manage-users") {
                                    if (!keycloak.authServerUrl) return "";

                                    const urlObj = new URL(keycloak.authServerUrl);
                                    const realm = keycloak.realm;
                                    const urlPrefix =
                                      `${urlObj.origin}${env.REACT_APP_KEYCLOAK_URL.startsWith("/") ? env.REACT_APP_KEYCLOAK_URL : ""}`.replace(
                                        /\/$/,
                                        "",
                                      );

                                    window.location.href = `${urlPrefix}/admin/${realm}/console/#/customer-portal-realm/users`;
                                  } else {
                                    navigate(route);
                                  }
                                }}
                                selected={location.pathname === route}
                                sx={{
                                  minHeight: 48,
                                  px: 1.5,
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 3,
                                    justifyContent: "center",
                                  }}
                                >
                                  {route === "/message-center" && totalUnread > 0 ? (
                                    <Badge badgeContent={totalUnread} color="error">
                                      <Icon />
                                    </Badge>
                                  ) : (
                                    <Icon />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={titleToUse}
                                  sx={{
                                    opacity: isDrawerOpen ? 1 : 0,
                                    transition: (theme) =>
                                      theme.transitions.create("opacity", {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen * 2,
                                      }),
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          </Tooltip>
                        );
                      })}
                    </List>
                  );
                })}
              </Stack>
            </Drawer>
          )}
          <Stack direction="column" height="100%" overflow="auto" position="relative" width="100%">
            <Toolbar variant="dense" />
            {downloadingImage && (
              <>
                <Backdrop sx={{ zIndex: theme.zIndex.drawer + 1 }} open />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    justifySelf: "center",
                    alignContent: "center",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                >
                  <CircularProgress size={150} />
                </Box>
              </>
            )}
            <Stack
              ref={layoutContainerRef}
              direction="column"
              flexGrow={1}
              justifyContent="center"
              overflow={downloadingImage ? "none" : "auto"}
            >
              {LegalAgreements !== false && LegalAgreements}
              {LegalAgreements === false && UserPreferences !== false && UserPreferences}
              {LegalAgreements === false && UserPreferences === false && (
                <>
                  <LayoutRoutes />
                  {!window.matchMedia("(pointer: fine)").matches && <Backdrop onClick={() => setIsDrawerOpen(false)} open={isDrawerOpen} />}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Footer
          frontendUrl={window.location.origin}
          isAuthenticated={!!keycloak.authenticated}
          lastPayDate={lastPayDate ?? null}
          theme={theme}
        />
      </Stack>
    </Stack>
  );
};

export default NavigationWrapper(Navigation);
